import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { useOidc } from '@axa-fr/react-oidc';
import { resetAnalytics, trackEvent } from 'providers/analytics';
import TermsAndConditionsContent from './TermsAndConditionsContent';

const SCROLL_TOLERANCE_PX = 4;

interface TermsAndConditionsDialogProps {
  open: boolean;
  isSubmitting: boolean;
  onAccept: () => void;
}

const TermsAndConditionsDialog: React.FC<TermsAndConditionsDialogProps> = ({
  open,
  isSubmitting,
  onAccept,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const { logout } = useOidc();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= SCROLL_TOLERANCE_PX) {
      setHasScrolledToBottom(true);
    }
  };

  const handleDecline = () => {
    trackEvent('user_logged_out');
    resetAnalytics();
    logout('/app');
  };

  const acceptDisabled = !hasScrolledToBottom || isSubmitting;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="terms-and-conditions-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'transparent',
          },
        },
      }}
    >
      <DialogTitle id="terms-and-conditions-title">
        <Trans id="Terms and Conditions">Terms and Conditions</Trans>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {hasScrolledToBottom ? (
            <Trans id="Thanks for reading. You can now accept the terms and conditions.">
              Thanks for reading. You can now accept the terms and conditions.
            </Trans>
          ) : (
            <Trans id="Please read the terms and conditions below. Scroll to the bottom to enable the accept button.">
              Please read the terms and conditions below. Scroll to the bottom to enable the accept
              button.
            </Trans>
          )}
        </Typography>
        <Box
          onScroll={handleScroll}
          tabIndex={0}
          sx={{
            maxHeight: '50vh',
            overflowY: 'auto',
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            p: 2,
            backgroundColor: (t) => t.palette.background.default,
          }}
        >
          <TermsAndConditionsContent />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          color="secondary"
          variant="text"
          onClick={handleDecline}
          disabled={isSubmitting}
        >
          {i18n._('Decline and sign out')}
        </Button>
        <Button
          color="primary"
          variant={hasScrolledToBottom ? 'contained' : 'outlined'}
          onClick={onAccept}
          disabled={acceptDisabled}
          sx={{
            minWidth: 140,
            transition: 'box-shadow 200ms ease, transform 200ms ease',
            ...(hasScrolledToBottom && !isSubmitting && {
              boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}33`,
            }),
          }}
        >
          {i18n._('Accept')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndConditionsDialog;
